import { sessionFetch } from '../../utils/sessionFetch';
import { useState, useMemo } from "react";
import moment from "moment";
import ExportAsExcel from "./ExportAsExcel";
import StatsDateSelector from "./StatsDateSelector";
import { COLLA, getServerEndpoint, isAssaigDeMusics, isReunio } from "../../utils/utils";
import { isHiddenStatsRehearsal } from './statsEventCategories';

function StatsExportTab({
    events,
    excludedEventIds,
    setExcludedEventIds,
    castellersInfo,
    filtersEvents,
    setFiltersEvents,
    assistenciesDict,
    chosenUsers
}) {
    const [start_date, setStart_date] = useState(moment().startOf('year').format('YYYY-MM-DD'))

    // Default: 31st December of this year
    const [end_date, setEnd_date] = useState(moment().endOf('year').format('YYYY-MM-DD'))

    const filteredEvents = useMemo(() => {
        return events
            .filter(e => moment(e['data-esperada-inici']).isBetween(start_date, end_date, 'day', '[]'))
            .filter(e => 'activitats' in filtersEvents && filtersEvents.activitats ? e.tipus !== 'activitat' : true)
            .filter(e => 'actuacions' in filtersEvents && filtersEvents.actuacions ? e.tipus !== 'actuació' : true)
            .filter(e => 'assaigs' in filtersEvents && filtersEvents.assaigs ? (e.tipus !== 'assaig' || isAssaigDeMusics(e)) : true)
            .filter(e => !isHiddenStatsRehearsal(e, filtersEvents))
            .filter(e => 'comercials' in filtersEvents && filtersEvents.comercials ? !e.title?.toLowerCase().includes('comercial') && !e.title?.toLowerCase().includes('pilar') : true)
            .filter(e => 'reunions' in filtersEvents && filtersEvents.reunions ? !isReunio(e) : true)
            .filter(e => filtersEvents.ids ? !filtersEvents.ids?.includes(e.id) : true)
            .filter(e => filtersEvents.titles ? !filtersEvents.titles?.includes(e.title) : true)
            .sort((ev_a, ev_b) => moment(ev_a?.['data-esperada-inici']).diff(moment(ev_b?.['data-esperada-inici'])))
    }, [events, start_date, end_date, filtersEvents])

    const [isExportingSheet, setIsExportingSheet] = useState(false)
    const [sheetUrl, setSheetUrl] = useState('')
    const [sheetError, setSheetError] = useState('')

    const exportAttendanceSheet = async () => {
        setIsExportingSheet(true)
        setSheetUrl('')
        setSheetError('')

        try {
            const response = await sessionFetch(`${getServerEndpoint(COLLA)}/api/export_attendance_range`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-api-key': '453dabb4-7645-4626-a1bb-477dff3aa557'
                },
                body: JSON.stringify({ start_date, end_date, excludedEventIds })
            })

            const data = await response.json()

            if (!response.ok || !data?.url) {
                throw new Error(data?.error || 'No s\'ha pogut crear el Google Sheet')
            }

            setSheetUrl(data.url)
            window.open(data.url, '_blank')
        } catch (error) {
            setSheetError(error.message)
        } finally {
            setIsExportingSheet(false)
        }
    }

    return (
        <>
            <StatsDateSelector
                start_date={start_date}
                setStart_date={setStart_date}
                end_date={end_date}
                setEnd_date={setEnd_date}
            />
            <div style={{
                marginTop: 20,
                padding: 20,
                backgroundColor: '#f8f9fa',
                borderRadius: 12,
                border: '2px solid #e9ecef'
            }}>
                <h2 style={{ marginTop: 0 }}>Exporta l'assistència a Google Sheets</h2>
                <p style={{ 
                    fontSize: 18, 
                    fontWeight: 500, 
                    color: '#495057',
                    marginBottom: 10
                }}>
                    📅 Del <strong>{moment(start_date).format('DD/MM/YYYY')}</strong> al <strong>{moment(end_date).format('DD/MM/YYYY')}</strong>
                </p>
                <p style={{ maxWidth: 700, color: '#6c757d' }}>
                    Crea un full amb tots els castellers i l'assistència a cada esdeveniment entre les dates seleccionades. Cada columna
                    correspondrà a un esdeveniment i cada fila a un casteller.
                </p>

                {/* Preview dels esdeveniments */}
                {(() => {
                    const eventsInRange = events
                        .filter(e => moment(e['data-esperada-inici']).isBetween(start_date, end_date, 'day', '[]'))
                        .sort((a, b) => moment(a['data-esperada-inici']).diff(moment(b['data-esperada-inici'])));
                    
                    const includedEvents = eventsInRange.filter(e => !excludedEventIds.includes(e.id));
                    const excludedEvents = eventsInRange.filter(e => excludedEventIds.includes(e.id));

                    return (
                        <div style={{ 
                            marginBottom: 15,
                            padding: 12,
                            backgroundColor: '#e8f4f8',
                            borderRadius: 8,
                            border: '1px solid #bee5eb'
                        }}>
                            <div style={{ 
                                fontWeight: 600, 
                                marginBottom: 8,
                                color: '#0c5460'
                            }}>
                                {includedEvents.length === 0 
                                    ? '⚠️ No hi ha esdeveniments en aquest rang de dates'
                                    : `📊 S'exportaran ${includedEvents.length} esdeveniment${includedEvents.length !== 1 ? 's' : ''}:`
                                }
                            </div>
                            {includedEvents.length > 0 ? (
                                <div style={{ 
                                    maxHeight: 150, 
                                    overflowY: 'auto',
                                    fontSize: 14
                                }}>
                                    {includedEvents.map((ev, idx) => (
                                        <div key={ev.id} style={{ 
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 8,
                                            padding: '4px 0',
                                            borderBottom: idx < includedEvents.length - 1 ? '1px solid #d1ecf1' : 'none'
                                        }}>
                                            <span style={{ 
                                                color: '#6c757d',
                                                minWidth: 50,
                                                fontSize: 13
                                            }}>
                                                {moment(ev['data-esperada-inici']).format('DD/MM')}
                                            </span>
                                            <span style={{
                                                padding: '2px 6px',
                                                borderRadius: 4,
                                                fontSize: 11,
                                                fontWeight: 500,
                                                backgroundColor: ev.tipus === 'actuació' ? '#6f42c1' : ev.tipus === 'assaig' ? '#28a745' : '#fd7e14',
                                                color: 'white',
                                                textTransform: 'uppercase'
                                            }}>
                                                {ev.tipus === 'actuació' ? 'ACTU' : ev.tipus === 'assaig' ? 'ASSA' : 'ACTV'}
                                            </span>
                                            <span style={{ color: '#212529', flex: 1 }}>{ev.title}</span>
                                            <button
                                                onClick={() => setExcludedEventIds(prev => [...prev, ev.id])}
                                                style={{
                                                    background: 'none',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    padding: '2px 6px',
                                                    fontSize: 14,
                                                    color: '#dc3545',
                                                    lineHeight: 1
                                                }}
                                                title="Excloure d'exportació"
                                            >
                                                ✕
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            ) : null}

                            {excludedEvents.length > 0 ? (
                                <div style={{ marginTop: 10 }}>
                                    <div style={{ 
                                        fontWeight: 600, 
                                        marginBottom: 8,
                                        color: '#856404',
                                        fontSize: 13
                                    }}>
                                        🚫 Exclosos ({excludedEvents.length}):
                                    </div>
                                    <div style={{ 
                                        maxHeight: 100, 
                                        overflowY: 'auto',
                                        fontSize: 13,
                                        opacity: 0.7
                                    }}>
                                        {excludedEvents.map((ev, idx) => (
                                            <div key={ev.id} style={{ 
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 8,
                                                padding: '3px 0',
                                                textDecoration: 'line-through'
                                            }}>
                                                <span style={{ color: '#6c757d', minWidth: 50, fontSize: 12 }}>
                                                    {moment(ev['data-esperada-inici']).format('DD/MM')}
                                                </span>
                                                <span style={{ color: '#6c757d', flex: 1 }}>{ev.title}</span>
                                                <button
                                                    onClick={() => setExcludedEventIds(prev => prev.filter(id => id !== ev.id))}
                                                    style={{
                                                        background: 'none',
                                                        border: 'none',
                                                        cursor: 'pointer',
                                                        padding: '2px 6px',
                                                        fontSize: 14,
                                                        color: '#28a745',
                                                        lineHeight: 1
                                                    }}
                                                    title="Tornar a incloure"
                                                >
                                                    ↩
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    );
                })()}

                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    <button
                        onClick={exportAttendanceSheet}
                        disabled={isExportingSheet || events.filter(e => moment(e['data-esperada-inici']).isBetween(start_date, end_date, 'day', '[]') && !excludedEventIds.includes(e.id)).length === 0}
                        style={{
                            padding: '12px 18px',
                            backgroundColor: isExportingSheet || events.filter(e => moment(e['data-esperada-inici']).isBetween(start_date, end_date, 'day', '[]') && !excludedEventIds.includes(e.id)).length === 0 ? '#adb5bd' : '#0d6efd',
                            color: 'white',
                            border: 'none',
                            borderRadius: 8,
                            cursor: isExportingSheet || events.filter(e => moment(e['data-esperada-inici']).isBetween(start_date, end_date, 'day', '[]') && !excludedEventIds.includes(e.id)).length === 0 ? 'not-allowed' : 'pointer',
                            fontSize: 16
                        }}
                    >
                        {isExportingSheet ? 'Creant el Google Sheet...' : 'Crear Google Sheet'}
                    </button>

                    {sheetUrl ? (
                        <a
                            href={sheetUrl}
                            target="_blank"
                            rel="noreferrer"
                            style={{
                                padding: '12px 18px',
                                backgroundColor: '#28a745',
                                color: 'white',
                                borderRadius: 8,
                                textDecoration: 'none',
                                fontSize: 16
                            }}
                        >
                            Obrir Google Sheets
                        </a>
                    ) : null}
                </div>

                {sheetError ? (
                    <div style={{ marginTop: 10, color: 'red' }}>
                        {sheetError}
                    </div>
                ) : null}
            </div>

            <div>
                <ExportAsExcel
                    castellersInfo={castellersInfo}
                    filteredEvents={filteredEvents}
                    filtersEvents={filtersEvents}
                    setFiltersEvents={setFiltersEvents}
                    assistenciesDict={assistenciesDict}
                    chosenUsers={chosenUsers}
                />
            </div>
        </>
    );
}

export default StatsExportTab;
